import { inject, Injectable, signal } from '@angular/core';
import {
  LocalstorageConstants,
  IClientData,
  IClient,
  IInviteMembersPayload,
  ITokenModel,
} from '@dss-libs/shared/type/common-types';
import { HttpClient, httpResource } from '@angular/common/http';
import { API_URL } from '@dss-libs/shared/utility/global-injection-tokens';
import { BehaviorSubject, EMPTY, finalize, Observable } from 'rxjs';
import { CLIENTS_URLS } from '../../urls/clients.urls';

@Injectable({
  providedIn: 'root',
})
export class ClientService {
  readonly #http = inject(HttpClient);
  readonly #apiUrl = inject(API_URL);
  readonly #resolvedToken = new BehaviorSubject<ITokenModel>({} as ITokenModel);

  get tokenData() {
    return this.#resolvedToken.value;
  }

  readonly clientDataResource = httpResource<IClientData>(() => `${this.#apiUrl}${CLIENTS_URLS.GET_CLIENT_LOGGED_IN}`);

  userClientsResource() {
    return httpResource<IClient[]>(() => `${this.#apiUrl}${CLIENTS_URLS.GET_USER_CLIENTS}`);
  }

  getClientActiveUsers = this.#http.get<number>(`${this.#apiUrl}${CLIENTS_URLS.GET_CLIENT_ACTIVE_USERS}`);

  readonly showSubscriptionModal = signal(false);
  readonly resolvedToken$ = this.#resolvedToken.asObservable();

  createClient(name: string): Observable<IClient> {
    const apiUrl = `${this.#apiUrl}${CLIENTS_URLS.CREATE_CLIENT}`;
    return this.#http.post<IClient>(apiUrl, { name });
  }

  updateResolvedToken(token: ITokenModel) {
    this.#resolvedToken.next(token);
  }

  saveInvitedEmailsLocally(emails: IInviteMembersPayload[]) {
    localStorage.setItem(LocalstorageConstants.INVITED_EMAILS, JSON.stringify(emails));
  }

  deleteEmailsSavedLocally() {
    localStorage.removeItem(LocalstorageConstants.INVITED_EMAILS);
  }

  inviteEmailsSavedLocally() {
    if (!localStorage.getItem(LocalstorageConstants.INVITED_EMAILS)) return EMPTY;
    const emails = JSON.parse(localStorage.getItem(LocalstorageConstants.INVITED_EMAILS)!);
    return this.#inviteMembers(emails).pipe(finalize(() => this.deleteEmailsSavedLocally()));
  }

  #inviteMembers(members: IInviteMembersPayload[]): Observable<void> {
    const apiUrl = `${this.#apiUrl}${CLIENTS_URLS.INVITE_MEMBERS}`;
    return this.#http.post<void>(apiUrl, members);
  }
}
